/**
 * @file ErrorPage.tsx
 *
 * @description Global error page component for handling application errors and route errors.
 * Displays user-friendly error messages with retry and navigation options.
 *
 * @module core
 *
 * @domain core
 *
 */

import { AlertTriangle, Home, Mail, RefreshCw, ChevronLeft } from "lucide-react";
import { type FC, useState } from "react";
import { useRouteError, isRouteErrorResponse } from "react-router-dom";

/**
 * Props for ErrorPage component
 */
interface ErrorPageProperties {
  errorMessage?: string;
  onGoBack?: () => void;
  onRetry?: () => void;
}

/**
 * ErrorPage - Global error page component
 *
 * Features:
 * - Displays error information from React Router
 * - Extracts error details (status, message, type)
 * - Provides retry functionality
 * - Navigation options (go back, home)
 * - Support contact information
 * - Development mode error details
 *
 * Error Types Handled:
 * - Route errors (404, 500, etc.)
 * - JavaScript errors
 * - String errors
 * - Unknown errors
 *
 * @param root0
 *
 * @param root0.errorMessage
 *
 * @param root0.onRetry
 *
 * @param root0.onGoBack
 *
 * @returns JSX element with error page UI
 *
 * @example
 * ```tsx
 * <ErrorPage
 *   errorMessage="Page not found"
 *   onRetry={() => window.location.reload()}
 *   onGoBack={() => navigate(-1)}
 * />
 * ```
 *
 */
export const ErrorPage: FC<ErrorPageProperties> = ({ errorMessage, onGoBack, onRetry }) => {
  const [isRetrying, setIsRetrying] = useState(false);

  // Get real error information from React Router
  const routeError = useRouteError();

  // Extract and normalize error information from various error types
  const getRealErrorInfo = () => {
    if (isRouteErrorResponse(routeError)) {
      const errorData = routeError.data as null | string | undefined | { message?: string };
      let message: string;
      if (typeof errorData === "object" && errorData !== null && "message" in errorData) {
        message = errorData.message ?? routeError.statusText;
      } else if (typeof errorData === "string") {
        message = errorData;
      } else {
        message = routeError.statusText;
      }
      return {
        isRouteError: true,
        message,
        status: routeError.status,
        statusText: routeError.statusText,
      };
    }

    if (routeError instanceof Error) {
      return {
        isRouteError: false,
        message: routeError.message,
        status: 500,
        statusText: "Internal Server Error",
      };
    }

    if (typeof routeError === "string") {
      return {
        isRouteError: false,
        message: routeError,
        status: 500,
        statusText: "Error",
      };
    }

    // Fallback for unknown errors
    return {
      isRouteError: false,
      message: errorMessage ?? "The requested resource could not be found on this server.",
      status: 404,
      statusText: "Not Found",
    };
  };

  const errorInfo = getRealErrorInfo();

  // Handle retry action (custom handler or page reload)
  const handleRetry = () => {
    if (onRetry) {
      setIsRetrying(true);
      try {
        onRetry();
        setIsRetrying(false);
      } catch {
        setIsRetrying(false);
      }
    } else {
      setIsRetrying(true);
      setTimeout(() => {
        globalThis.location.reload();
      }, 1000);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full text-center">
        {/* Error Icon */}
        <div className="mb-8">
          <div className="inline-flex items-center justify-center w-24 h-24 bg-red-900/30 rounded-full mb-6 border border-red-800/50">
            <AlertTriangle className="w-12 h-12 text-red-400" />
          </div>
        </div>

        {/* Error Details */}
        <div className="mb-10 space-y-4">
          <h1 className="text-4xl md:text-5xl font-bold text-slate-100">
            Error {errorInfo.status}
          </h1>
          <h2 className="text-2xl md:text-3xl font-semibold text-slate-200">
            {errorInfo.statusText}
          </h2>
          <p className="text-lg text-slate-300 max-w-lg mx-auto leading-relaxed">
            {errorInfo.message}
          </p>
          <p className="text-sm text-slate-400 max-w-md mx-auto">
            {(() => {
              if (errorInfo.status === 404) {
                return "Please check the URL or navigate back to the homepage.";
              }
              if (errorInfo.status === 500) {
                return "Please try again later or contact our support team.";
              }
              return "Please try again or contact our support team.";
            })()}
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          {onGoBack ? (
            <button
              className="inline-flex items-center gap-2 px-6 py-3 bg-slate-700 hover:bg-slate-600 text-slate-200 font-medium rounded-lg transition-colors duration-200 border border-slate-600"
              onClick={onGoBack}
            >
              <ChevronLeft className="w-4 h-4" />
              Go Back
            </button>
          ) : null}

          <button
            className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-500 disabled:bg-blue-700 text-white font-medium rounded-lg transition-colors duration-200"
            disabled={isRetrying}
            onClick={handleRetry}
          >
            <RefreshCw className={`w-4 h-4 ${isRetrying ? "animate-spin" : ""}`} />
            {isRetrying ? "Retrying..." : "Try Again"}
          </button>

          <a
            className="inline-flex items-center gap-2 px-6 py-3 bg-slate-200 hover:bg-white text-slate-900 font-medium rounded-lg transition-colors duration-200"
            href="/"
          >
            <Home className="w-4 h-4" />
            Homepage
          </a>
        </div>

        {/* Support Contact */}
        <div className="mt-12 pt-8 border-t border-slate-700">
          <p className="text-sm text-slate-400 mb-4">
            Still having trouble? Our support team is here to help.
          </p>
          <a
            className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 font-medium transition-colors duration-200"
            href="mailto:support@company.com"
          >
            <Mail className="w-4 h-4" />
            Contact Support
          </a>
        </div>

        {/* Error ID for debugging */}
        {process.env.NODE_ENV === "development" && (
          <div className="mt-8 p-4 bg-slate-800 rounded-lg border border-slate-700">
            <div className="space-y-2">
              <p className="text-xs text-slate-400 font-mono">
                Error ID: {Date.now().toString(36)}
              </p>
              <p className="text-xs text-slate-400 font-mono">
                Status: {errorInfo.status} - {errorInfo.statusText}
              </p>
              <p className="text-xs text-slate-400 font-mono">
                Route Error: {errorInfo.isRouteError ? "Yes" : "No"}
              </p>
              {routeError ? (
                <details className="mt-2">
                  <summary className="text-xs text-slate-400 cursor-pointer hover:text-slate-300">
                    Raw Error Details
                  </summary>
                  <pre className="text-xs text-slate-500 mt-2 p-2 bg-slate-900 rounded overflow-x-auto">
                    {JSON.stringify(routeError, null, 2)}
                  </pre>
                </details>
              ) : null}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
